import { useCallback, useEffect, useMemo, useState } from 'react';
import { COMMENT_BODY_MAX, REPORT_REASONS, handleLabel, type BlogComment, type ReportReasonKey } from '@gadenin/contract';
import type { BlogParticipationClient } from '../../infrastructure/blog/BlogParticipationClient';
import type { Analytics } from '../../application/analytics/analytics';

/**
 * 記事詳細の参加 UI (helpful + コメント + 通報)。
 *
 * - **本文の表示はこのコンポーネントに依存しない** (UW-BLOG 共通受け入れ条件)。
 *   参加 API が落ちても、ここだけが再試行可能なエラー表示になる
 * - コメント本文は**必ずテキストとして描画**する (React の既定エスケープ)。
 *   リンク化・画像化はしない
 * - 表示可否 (hidden / deleted) はサーバが決める。ここでは受け取ったものだけを描く
 */
export type BlogParticipationProps = {
  articleId: string;
  client: BlogParticipationClient;
  analytics: Analytics;
  /** 未認証時のサインイン先。現在ページへ戻れるよう next を付ける */
  signInHref: string;
};

type Viewer = { kind: 'loading' } | { kind: 'anonymous' } | { kind: 'signedIn'; userId: string };

export function BlogParticipation({ articleId, client, analytics, signInHref }: BlogParticipationProps) {
  const [viewer, setViewer] = useState<Viewer>({ kind: 'loading' });
  const [comments, setComments] = useState<BlogComment[] | null>(null);
  const [helpfulCount, setHelpfulCount] = useState<number | null>(null);
  const [helpfulMine, setHelpfulMine] = useState(false);
  const [reportedIds, setReportedIds] = useState<Set<string>>(new Set());
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoadError(false);
    const [commentsResult, reactionsResult] = await Promise.all([
      client.comments(articleId),
      client.reactions(articleId),
    ]);
    if (!commentsResult.ok || !reactionsResult.ok) {
      setLoadError(true);
      return;
    }
    setComments(commentsResult.value);
    setHelpfulCount(reactionsResult.value.helpful);
  }, [client, articleId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // サインイン状態と自分の userId は /bff/session が正。ブログ側で独自判定しない
      try {
        const response = await fetch('/bff/session', { headers: { Accept: 'application/json' } });
        const session = response.ok ? await response.json() as { authenticated?: boolean; userId?: string } : null;
        if (cancelled) return;
        if (session?.authenticated && session.userId) {
          setViewer({ kind: 'signedIn', userId: session.userId });
          const me = await client.me(articleId);
          if (!cancelled && me.ok) {
            setHelpfulMine(me.value.helpful);
            setReportedIds(new Set(me.value.reportedCommentIds));
          }
        } else {
          setViewer({ kind: 'anonymous' });
        }
      } catch {
        if (!cancelled) setViewer({ kind: 'anonymous' });
      }
    })();
    return () => { cancelled = true; };
  }, [client, articleId]);

  return (
    <div className="stack stack--lg blog-participation">
      <HelpfulButton
        articleId={articleId} client={client} analytics={analytics}
        viewer={viewer} signInHref={signInHref}
        count={helpfulCount} mine={helpfulMine}
        onChange={(count, mine) => { setHelpfulCount(count); setHelpfulMine(mine); }}
      />
      {loadError ? (
        <div className="blog-participation__error" role="alert">
          <p className="muted">コメントを読み込めませんでした。記事はこのままお読みいただけます。</p>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => void load()}>再試行</button>
        </div>
      ) : (
        <CommentSection
          articleId={articleId} client={client} analytics={analytics}
          viewer={viewer} signInHref={signInHref}
          comments={comments} reportedIds={reportedIds}
          onReported={(commentId) => setReportedIds((previous) => new Set([...previous, commentId]))}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

function HelpfulButton({ articleId, client, analytics, viewer, signInHref, count, mine, onChange }: {
  articleId: string;
  client: BlogParticipationClient;
  analytics: Analytics;
  viewer: Viewer;
  signInHref: string;
  count: number | null;
  mine: boolean;
  onChange: (count: number, mine: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const toggle = async () => {
    if (viewer.kind !== 'signedIn') {
      window.location.assign(signInHref);
      return;
    }
    if (busy || count === null) return;
    setBusy(true);
    setFailed(false);
    const next = !mine;
    const result = await client.setHelpful(articleId, next);
    setBusy(false);
    if (result.ok) {
      onChange(result.value.helpful, next);
      analytics.track({ name: 'web_blog_helpful_toggle', articleId, to: next ? 'on' : 'off' });
    } else {
      setFailed(true);
      analytics.track({
        name: 'web_blog_participation_error', articleId, op: 'reaction',
        status: result.failure.kind === 'unauthenticated' ? 401 : 0,
      });
    }
  };

  return (
    <div className="blog-helpful">
      <button
        type="button"
        className={mine ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
        aria-pressed={mine}
        disabled={busy || count === null}
        onClick={() => void toggle()}
      >
        👍 参考になった{count !== null && <span className="blog-helpful__count">{count}</span>}
      </button>
      {viewer.kind === 'anonymous' && <span className="t-caption muted">リアクションにはサインインが必要です</span>}
      {failed && <span className="t-caption blog-participation__inline-error" role="alert">送信できませんでした。もう一度お試しください</span>}
    </div>
  );
}

function CommentSection({ articleId, client, analytics, viewer, signInHref, comments, reportedIds, onReported, onChanged }: {
  articleId: string;
  client: BlogParticipationClient;
  analytics: Analytics;
  viewer: Viewer;
  signInHref: string;
  comments: BlogComment[] | null;
  reportedIds: Set<string>;
  onReported: (commentId: string) => void;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const trimmed = draft.trim();
  const canSubmit = trimmed.length >= 1 && trimmed.length <= COMMENT_BODY_MAX && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setSubmitError(null);
    const result = await client.postComment(articleId, trimmed);
    setBusy(false);
    if (result.ok) {
      setDraft('');
      analytics.track({ name: 'web_blog_comment_post', articleId, isEdit: false, bodyLength: trimmed.length });
      onChanged();
    } else {
      setSubmitError('コメントを投稿できませんでした。時間をおいて再度お試しください。');
      analytics.track({ name: 'web_blog_participation_error', articleId, op: 'comment', status: result.failure.kind === 'unauthenticated' ? 401 : 0 });
    }
  };

  const saveEdit = async (commentId: string) => {
    const body = editDraft.trim();
    if (body.length < 1 || body.length > COMMENT_BODY_MAX || busy) return;
    setBusy(true);
    const result = await client.editComment(commentId, body);
    setBusy(false);
    if (result.ok) {
      setEditingId(null);
      analytics.track({ name: 'web_blog_comment_post', articleId, isEdit: true, bodyLength: body.length });
      onChanged();
    } else {
      setSubmitError('コメントを更新できませんでした。');
    }
  };

  const remove = async (commentId: string) => {
    if (!window.confirm('このコメントを削除しますか？削除すると元に戻せません。')) return;
    const result = await client.deleteComment(commentId);
    if (result.ok) {
      analytics.track({ name: 'web_blog_comment_delete', articleId });
      onChanged();
    } else {
      setSubmitError('コメントを削除できませんでした。');
    }
  };

  return (
    <section className="stack" aria-label="コメント">
      <h2 className="t-heading">コメント{comments && <span className="muted"> {comments.length}</span>}</h2>

      {viewer.kind === 'signedIn' ? (
        <div className="stack stack--sm">
          <textarea
            className="blog-comment-input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="この記事への考えや質問を書く"
            rows={3}
            aria-label="コメントを書く"
            maxLength={COMMENT_BODY_MAX + 100}
          />
          <div className="blog-comment-input__foot">
            <span className={trimmed.length > COMMENT_BODY_MAX ? 't-caption blog-participation__inline-error' : 't-caption muted'}>
              {trimmed.length} / {COMMENT_BODY_MAX}
            </span>
            <button type="button" className="btn btn--primary btn--sm" disabled={!canSubmit} onClick={() => void submit()}>
              コメントを投稿
            </button>
          </div>
        </div>
      ) : viewer.kind === 'anonymous' ? (
        <p className="blog-signin-note">
          コメントの投稿には Gadenin アカウントが必要です。
          <a className="btn btn--ghost btn--sm" href={signInHref}>サインインして参加する</a>
        </p>
      ) : null}

      {submitError && <p className="blog-participation__inline-error" role="alert">{submitError}</p>}

      {comments === null ? (
        <p className="muted">コメントを読み込んでいます…</p>
      ) : comments.length === 0 ? (
        <p className="muted">まだコメントはありません。最初の考えを聞かせてください。</p>
      ) : (
        <ul className="blog-comments">
          {comments.map((comment) => (
            <li key={comment.id} className="blog-comment">
              <div className="blog-comment__head">
                <strong>{comment.author.displayName ?? comment.author.handle ?? '利用者'}</strong>
                {handleLabel(comment.author.handle) && <span className="muted t-caption">{handleLabel(comment.author.handle)}</span>}
                <time className="muted t-caption" dateTime={comment.createdAt}>{formatDateTime(comment.createdAt)}</time>
                {comment.edited && <span className="muted t-caption">(編集済み)</span>}
              </div>

              {editingId === comment.id ? (
                <div className="stack stack--sm">
                  <textarea
                    className="blog-comment-input"
                    value={editDraft}
                    onChange={(event) => setEditDraft(event.target.value)}
                    rows={3}
                    aria-label="コメントを編集"
                  />
                  <div className="blog-comment-input__foot">
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditingId(null)}>キャンセル</button>
                    <button type="button" className="btn btn--primary btn--sm" onClick={() => void saveEdit(comment.id)}>保存</button>
                  </div>
                </div>
              ) : (
                // 本文は必ずテキストとして描画する。URL をリンク化しない
                <p className="blog-comment__body">{comment.body}</p>
              )}

              <div className="blog-comment__actions">
                {viewer.kind === 'signedIn' && viewer.userId === comment.author.userId ? (
                  <>
                    <button
                      type="button" className="btn btn--ghost btn--sm"
                      onClick={() => { setEditingId(comment.id); setEditDraft(comment.body); }}
                    >編集</button>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => void remove(comment.id)}>削除</button>
                  </>
                ) : (
                  <ReportButton
                    comment={comment} articleId={articleId} client={client} analytics={analytics}
                    viewer={viewer} signInHref={signInHref}
                    reported={reportedIds.has(comment.id)} onReported={onReported}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReportButton({ comment, articleId, client, analytics, viewer, signInHref, reported, onReported }: {
  comment: BlogComment;
  articleId: string;
  client: BlogParticipationClient;
  analytics: Analytics;
  viewer: Viewer;
  signInHref: string;
  reported: boolean;
  onReported: (commentId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReasonKey>('spam');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const reasons = useMemo(() => REPORT_REASONS, []);

  if (reported) return <span className="muted t-caption">通報済み</span>;

  const send = async () => {
    setBusy(true);
    setFailed(false);
    // 初回 201 / 重複再送 200 — どちらも成功 (BE が 1 件に畳む)
    const result = await client.reportComment(comment.id, reason);
    setBusy(false);
    if (result.ok) {
      setOpen(false);
      onReported(comment.id);
      analytics.track({ name: 'web_blog_comment_report', articleId, reasonCode: reason });
    } else {
      setFailed(true);
      analytics.track({ name: 'web_blog_participation_error', articleId, op: 'report', status: result.failure.kind === 'unauthenticated' ? 401 : 0 });
    }
  };

  return (
    <span className="blog-report">
      <button
        type="button" className="btn btn--ghost btn--sm"
        onClick={() => {
          if (viewer.kind !== 'signedIn') { window.location.assign(signInHref); return; }
          setOpen((previous) => !previous);
        }}
        aria-expanded={open}
      >通報</button>
      {open && (
        <span className="blog-report__form">
          <select
            aria-label="通報の理由" value={reason}
            onChange={(event) => setReason(event.target.value as ReportReasonKey)}
          >
            {reasons.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
          </select>
          <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => void send()}>送信</button>
          {failed && <span className="t-caption blog-participation__inline-error" role="alert">送信できませんでした</span>}
        </span>
      )}
    </span>
  );
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  // ブログ系 API の日時は ISO 8601。表示は Asia/Tokyo 固定 (リポジトリ契約)
  return new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
}
